/**
 * ShootingRangesTab - Admin zakładka do zarządzania strzelnicami
 * wojskaspecjalne.pl
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Switch,
  Textarea,
} from '../ui';
import {
  createShootingRange,
  getAllShootingRanges,
  updateShootingRange,
  type ShootingRange,
} from '../../lib/shootingRangesService';

interface ShootingRangesTabProps {
  onToast?: (type: 'success' | 'error', text: string) => void;
}

export const ShootingRangesTab: React.FC<ShootingRangesTabProps> = ({ onToast }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ranges, setRanges] = useState<ShootingRange[]>([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ShootingRange | null>(null);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    address: '',
    city: '',
    country: 'Poland',
    website_url: '',
    phone: '',
    email: '',
    is_active: true,
    display_order: 0,
  });

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getAllShootingRanges({ includeInactive: true, sortByOrder: true });
      if (!res.success) {
        setError(res.error || 'Błąd ładowania strzelnic');
        return;
      }
      setRanges(res.data || []);
    } catch (e: any) {
      console.error('[ShootingRangesTab] loadAll error:', e);
      setError(e?.message || 'Błąd ładowania strzelnic');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({
      name: '',
      slug: '',
      description: '',
      address: '',
      city: '',
      country: 'Poland',
      website_url: '',
      phone: '',
      email: '',
      is_active: true,
      display_order: 0,
    });
    setDialogOpen(true);
  };

  const openEdit = (r: ShootingRange) => {
    setEditing(r);
    setForm({
      name: r.name || '',
      slug: r.slug || '',
      description: r.description || '',
      address: r.address || '',
      city: r.city || '',
      country: r.country || 'Poland',
      website_url: r.website_url || '',
      phone: r.phone || '',
      email: r.email || '',
      is_active: !!r.is_active,
      display_order: r.display_order ?? 0,
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    try {
      if (!form.name.trim()) {
        onToast?.('error', 'Nazwa strzelnicy jest wymagana');
        return;
      }

      const payload = {
        name: form.name,
        slug: form.slug || null,
        description: form.description || null,
        address: form.address || null,
        city: form.city || null,
        country: form.country || null,
        website_url: form.website_url || null,
        phone: form.phone || null,
        email: form.email || null,
        is_active: form.is_active,
        display_order: Number.isFinite(form.display_order) ? form.display_order : 0,
      };

      const res = editing
        ? await updateShootingRange({ id: editing.id, ...payload })
        : await createShootingRange(payload);

      if (!res.success) throw new Error(res.error || 'Błąd zapisu strzelnicy');

      onToast?.('success', editing ? 'Strzelnica zaktualizowana' : 'Strzelnica dodana');
      setDialogOpen(false);
      await loadAll();
    } catch (e: any) {
      console.error('[ShootingRangesTab] submit error:', e);
      onToast?.('error', e?.message || 'Błąd zapisu strzelnicy');
    }
  };

  const toggleActive = async (r: ShootingRange) => {
    try {
      const res = await updateShootingRange({ id: r.id, is_active: !r.is_active });
      if (!res.success) throw new Error(res.error || 'Błąd zmiany statusu');
      await loadAll();
    } catch (e: any) {
      onToast?.('error', e?.message || 'Błąd zmiany statusu strzelnicy');
    }
  };

  if (loading) {
    return <div className="admin-loading">Ładowanie strzelnic…</div>;
  }

  return (
    <div className="admin-content">
      <div className="admin-content-header">
        <h2 className="admin-content-title">Strzelnice</h2>
        <p className="content-subtitle">Słownik miejsc szkoleniowych</p>
      </div>

      {error && (
        <Alert variant="error" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Lista strzelnic</h2>
          <Button variant="solid" onClick={openAdd}>
            + Dodaj strzelnicę
          </Button>
        </div>

        {ranges.length === 0 ? (
          <Alert>
            <AlertDescription>Brak strzelnic. Dodaj pierwszą strzelnicę.</AlertDescription>
          </Alert>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nazwa</th>
                  <th>Miasto</th>
                  <th>Adres</th>
                  <th>Status</th>
                  <th>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {ranges.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.name}</strong>
                      {r.description ? <div className="book-description">{r.description}</div> : null}
                    </td>
                    <td>{r.city || '—'}</td>
                    <td>{r.address || '—'}</td>
                    <td>
                      <Badge variant="secondary">{r.is_active ? 'Aktywna' : 'Nieaktywna'}</Badge>
                    </td>
                    <td>
                      <div className="admin-table-actions">
                        <Button variant="outline" size="sm" onClick={() => openEdit(r)}>
                          Edytuj
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleActive(r)}
                          className={r.is_active ? 'border-orange-500 text-orange-500 hover:bg-orange-500/10' : ''}
                        >
                          {r.is_active ? 'Dezaktywuj' : 'Aktywuj'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent size="4">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edytuj strzelnicę' : 'Dodaj strzelnicę'}</DialogTitle>
            <DialogDescription>Używane przy tworzeniu grup szkoleniowych.</DialogDescription>
          </DialogHeader>

          <Card variant="soft">
            <CardContent className="space-y-3">
              <div>
                <Label>Nazwa *</Label>
                <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} />
              </div>

              <div>
                <Label>Slug</Label>
                <Input value={form.slug} onChange={(e) => setForm((s) => ({ ...s, slug: e.target.value }))} />
              </div>

              <div>
                <Label>Opis</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                />
              </div>

              <div>
                <Label>Adres</Label>
                <Input value={form.address} onChange={(e) => setForm((s) => ({ ...s, address: e.target.value }))} />
              </div>

              <div>
                <Label>Miasto</Label>
                <Input value={form.city} onChange={(e) => setForm((s) => ({ ...s, city: e.target.value }))} />
              </div>

              <div>
                <Label>Kraj</Label>
                <Input value={form.country} onChange={(e) => setForm((s) => ({ ...s, country: e.target.value }))} />
              </div>

              <div>
                <Label>Strona WWW</Label>
                <Input
                  value={form.website_url}
                  onChange={(e) => setForm((s) => ({ ...s, website_url: e.target.value }))}
                />
              </div>

              <div>
                <Label>Telefon</Label>
                <Input value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} />
              </div>

              <div>
                <Label>Email</Label>
                <Input value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} />
              </div>

              <div className="flex items-center justify-between">
                <Label>Aktywna</Label>
                <Switch checked={form.is_active} onCheckedChange={(checked) => setForm((s) => ({ ...s, is_active: checked }))} />
              </div>
            </CardContent>
          </Card>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Anuluj
            </Button>
            <Button variant="solid" onClick={submit}>
              Zapisz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


